import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { subject } from '@casl/ability';
import moment from 'moment';
import cn from 'classnames';
import { Col, Divider, Form, Row, Select as SelectAntd } from 'antd';
import { DatePicker, Input, Button, BUTTON_TYPES, Opening, Select, AtsSkeleton } from '_atoms';
import { Typography, TYPOGRAPHY_WEIGHT } from '_atoms/Typography';
import { dateFormatter } from 'helpers/dateFormatter';
import { ACTIONS, Can, UI } from 'permission';
import { ReactComponent as Arrow } from 'dist/icons/Vector1.svg';
import { getOpeningsList } from 'actions';
import type { Opening as OpeningEntity } from 'store/reducers/jobReducer';
import type { DashboardOfferDecision } from 'actions/dashboardActions';
import type { Uuid } from 'store/reducers/types';
import { RULES } from './constants';
import './style.scss';

type OpeningOption = OpeningEntity & { current?: boolean; value?: Uuid; label?: string };

interface ResolutionProps {
  className?: string;
  openingCandidate?: OpeningEntity | null;
  userData: { offer_decision: DashboardOfferDecision };
  loading?: boolean;
  onSubmit: (data: Record<string, unknown>) => void;
  jobName?: string;
  jobStatus?: string;
}

export const Resolution: React.FC<ResolutionProps> = ({
  className,
  openingCandidate,
  userData,
  loading,
  onSubmit,
  jobName,
  jobStatus,
}) => {
  const { t } = useTranslation();

  const { id: jobId } = useParams<{ id: string }>();
  const [form] = Form.useForm();

  const [ediMode, setEditMode] = useState(false);
  const [open, setOpen] = useState(true);
  const [openings, setOpenings] = useState<OpeningOption[]>([]);

  const onOpenDetails = () => setOpen(o => !o);
  const onEdit = () => setEditMode(true);
  const onCancelEdit = () => {
    setEditMode(false);
    const location = openingCandidate?.offices.find(l => l.uuid === openingCandidate?.hired_location);
    form.setFieldsValue({
      probation_period: userData.offer_decision.probation_period,
      entry_date: moment(userData.offer_decision.entry_date, 'YYYY-MM-DD'),
      opening: {
        value: openingCandidate?.uuid,
        label: `${openingCandidate?.level} - ${openingCandidate?.team_lead.full_name}`,
        offices: openingCandidate?.offices || [],
      },
      location: { value: location?.uuid, label: location?.name },
    });
  };

  const onSubmitEntryDate = ({ entry_date, probation_period, opening, location }) => {
    onSubmit({
      entry_date: entry_date.format('YYYY-MM-DD'),
      probation_period,
      opening_id: opening.value,
      location_id: location.value,
    });
    setEditMode(false);
  };

  useEffect(() => {
    const location = openingCandidate?.offices.find(l => l.uuid === openingCandidate?.hired_location);

    form.setFieldsValue({
      probation_period: userData.offer_decision.probation_period,
      entry_date: moment(userData.offer_decision.entry_date, 'YYYY-MM-DD'),
      opening: {
        value: openingCandidate?.uuid,
        label: `${openingCandidate?.level} - ${openingCandidate?.team_lead.full_name}`,
        offices: openingCandidate?.offices || [],
      },
      location: { value: location?.uuid, label: location?.name },
    });
    if (openingCandidate) {
      getOpeningsList(jobId, { status: 'Open' })
        .then(openOpenings =>
          setOpenings([
            { ...openingCandidate, current: true },
            ...openOpenings.map(o => ({
              ...o,
              value: o.uuid,
              label: `${o.level} - ${o.team_lead.full_name}`,
            })),
          ])
        )
        .catch(() => setOpenings([]));
    }
  }, [openingCandidate]);

  if (loading) {
    return (
      <div className={cn('resolution resolution-loading', className)}>
        <div className="resolution__header">
          <Typography.Title2 weight={TYPOGRAPHY_WEIGHT.BOLD}>{t('translation:resolution.title')}</Typography.Title2>
        </div>
        <AtsSkeleton height={340} />
        <Divider />
      </div>
    );
  }

  if (ediMode) {
    return (
      <div className={cn('resolution', className, { open })}>
        <div className="resolution__header">
          <Typography.Title2 weight={TYPOGRAPHY_WEIGHT.BOLD}>{t('translation:resolution.title')}</Typography.Title2>
          <Arrow className="open-details" onClick={onOpenDetails} />
        </div>
        <div className="resolution__content">
          <Form form={form} onFinish={onSubmitEntryDate} validateTrigger="onSubmit">
            <Row gutter={12}>
              <Col flex="160px">
                <DatePicker
                  label={t('translation:resolution.label_entry_date')}
                  itemProps={{ name: 'entry_date', rules: RULES.DATE }}
                />
              </Col>
            </Row>
            <Row gutter={12}>
              <Col flex="160px">
                <Input
                  label={t('translation:resolution.label_probation_period')}
                  placeholder={t('translation:resolution.ph_probation_period')}
                  itemProps={{ name: 'probation_period' }}
                />
              </Col>
              <Col>
                <Typography.Text className="months">{t('translation:resolution.text_months')}</Typography.Text>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={24} lg={12} md={12} sm={24}>
                <Select
                  labelInValue
                  label={t('translation:resolution.label_opening')}
                  className="margin-bottom-20"
                  placeholder={t('translation:resolution.ph_opening')}
                  itemProps={{ name: 'opening' }}
                  onChange={(opening: OpeningOption) => {
                    const location =
                      opening.offices?.length === 1
                        ? { label: opening.offices[0].name, value: opening.offices[0].uuid }
                        : undefined;
                    form.setFieldsValue({ opening, location });
                  }}
                >
                  {openings.map(i => (
                    <SelectAntd.Option
                      key={i.uuid}
                      value={i.uuid}
                      label={`${i.level} - ${i.team_lead.full_name}`}
                      offices={i.offices}
                    >
                      <div className="opening-select-option">
                        <Typography.Text>
                          {`${i.level} - ${i.team_lead.full_name}`}
                          {i.current && <span> {t('translation:resolution.text_current')}</span>}
                        </Typography.Text>
                        <Typography.Caption>
                          {t('translation:resolution.label_expected_date')}: {dateFormatter(i.expected)}
                        </Typography.Caption>
                        <Typography.Caption>{i.offices.map(o => o.name).join(', ')}</Typography.Caption>
                      </div>
                    </SelectAntd.Option>
                  ))}
                </Select>
              </Col>
            </Row>
            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.opening !== curr.opening}>
              {({ getFieldValue }) => (
                <Row gutter={12}>
                  <Col span={24} lg={12} md={12} sm={24}>
                    <Select
                      labelInValue
                      label={t('translation:resolution.label_location')}
                      className="margin-bottom-20"
                      placeholder={t('translation:resolution.ph_location')}
                      options={(getFieldValue('opening') as OpeningOption | undefined)?.offices?.map(i => ({
                        label: i.name,
                        value: i.uuid,
                      }))}
                      itemProps={{ name: 'location' }}
                      disabled={(getFieldValue('opening') as OpeningOption | undefined)?.offices?.length === 1}
                    />
                  </Col>
                </Row>
              )}
            </Form.Item>
            <div className="resolution__content-footer">
              <Button onClick={onCancelEdit}>{t('translation:resolution.button_cancel')}</Button>
              <Button htmlType="submit" type={BUTTON_TYPES.PRIMARY}>
                {t('translation:resolution.button_save')}
              </Button>
            </div>
          </Form>
          {openingCandidate && (
            <Opening
              opening={openingCandidate}
              jobName={jobName}
              inResolution
              removeOpening={() => {}}
              closeOpening={() => {}}
              pauseOpening={() => {}}
              reopenOpening={() => {}}
              approveDeclineOpening={() => {}}
            />
          )}
        </div>
        <Divider />
      </div>
    );
  }

  if (!openingCandidate || !Object.keys(openingCandidate).length) return null;

  return (
    <div className={cn('resolution', className, { open })}>
      <div className="resolution__header">
        <Typography.Title2 weight={TYPOGRAPHY_WEIGHT.BOLD}>{t('translation:resolution.title')}</Typography.Title2>
        <Arrow className="open-details" onClick={onOpenDetails} />
      </div>
      <div className="resolution__content">
        <div className="info-block">
          <Typography.Label>{t('translation:resolution.label_entry_date')}</Typography.Label>
          <Typography.Text>
            {moment(userData.offer_decision.entry_date, 'YYYY-MM-DD')?.format('MMM DD, YYYY')}
          </Typography.Text>
        </div>
        <div className="info-block">
          <Typography.Label>{t('translation:resolution.label_probation_period')}</Typography.Label>
          <Typography.Text>
            {`${userData.offer_decision.probation_period} ${t('translation:resolution.text_months')}`}
          </Typography.Text>
        </div>
        <Can I={ACTIONS.READ} this={subject(UI.EDIT_RESOLUTION, { status: jobStatus })}>
          <Button onClick={onEdit} className="edit-button">
            {t('translation:resolution.button_edit')}
          </Button>
        </Can>
        {openingCandidate && (
          <Opening
            opening={openingCandidate}
            jobName={jobName}
            inResolution
            removeOpening={() => {}}
            closeOpening={() => {}}
            pauseOpening={() => {}}
            reopenOpening={() => {}}
            approveDeclineOpening={() => {}}
          />
        )}
      </div>
      <Divider />
    </div>
  );
};
